import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { AppDispatch, RootState } from 'store'
import {
  cancelFuelDeliveryRequest,
  getFuelDeliveryRequest,
} from 'features/fuelManagement/fuelManagementSlice'
import { IFuelDeliveryRequestLine } from 'types/api/fuelDeliveryRequestsApi.interface'
import { FuelDeliveryRequestStatus } from 'types/fuelEnums.interface'
import {
  fuelDeliveryFuelTypeLabel,
  fuelDeliveryRequestStatusLabel,
} from '../fuelData'
import { FuelDeliveryRequestFormModal } from './FuelDeliveryRequestFormModal'
import { RecordFuelDeliveryBatchModal } from './RecordFuelDeliveryBatchModal'
import useModalClose from 'hooks/useModalClose'
import { formatDateForDisplay } from 'utils/helpers'

const statusTone = (status: number): any => {
  if (status === FuelDeliveryRequestStatus.Cancelled) return 'danger'
  if (status === FuelDeliveryRequestStatus.Fulfilled) return 'success'
  if (
    status === FuelDeliveryRequestStatus.PartiallyReceived ||
    status === FuelDeliveryRequestStatus.PartiallyFulfilled
  ) {
    return 'accent'
  }
  if (status === FuelDeliveryRequestStatus.Received) return 'info'
  return 'neutral'
}

const canEditOrCancel = (status: number) =>
  status === FuelDeliveryRequestStatus.Requested ||
  status === FuelDeliveryRequestStatus.InProcurement

const canRecordBatch = (status: number) =>
  status === FuelDeliveryRequestStatus.Received ||
  status === FuelDeliveryRequestStatus.PartiallyReceived ||
  status === FuelDeliveryRequestStatus.PartiallyFulfilled

export const FuelDeliveryRequestDetailModal = ({
  requestId,
  closeModal,
}: {
  requestId: number
  closeModal: () => void
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { singleDeliveryRequest } = useSelector((store: RootState) => store.fuelManagement)
  const [editOpen, setEditOpen] = useState(false)
  const [batchLine, setBatchLine] = useState<IFuelDeliveryRequestLine | null>(null)
  useModalClose(closeModal)

  useEffect(() => {
    dispatch(getFuelDeliveryRequest(requestId))
  }, [dispatch, requestId])

  const request = singleDeliveryRequest?.id === requestId ? singleDeliveryRequest : null

  const refresh = () => dispatch(getFuelDeliveryRequest(requestId))

  const handleCancel = async () => {
    const resp = await dispatch(cancelFuelDeliveryRequest(requestId))
    if (resp.meta.requestStatus === 'fulfilled') refresh()
  }

  const lineColumns: IHonestTableColumn<IFuelDeliveryRequestLine>[] = [
    {
      id: 'fuelType',
      header: 'Fuel type',
      cell: (line) => fuelDeliveryFuelTypeLabel(line.fuelType),
    },
    {
      id: 'quantity',
      header: 'Quantity',
      cell: (line) => `${line.quantity} ${line.unit?.name || ''}`.trim(),
    },
    {
      id: 'tank',
      header: 'Tank',
      cell: (line) => line.fuelTank?.name || '—',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (line) => (
        <StatusPill status={statusTone(line.status)}>
          {fuelDeliveryRequestStatusLabel(line.status)}
        </StatusPill>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (line) =>
        canRecordBatch(line.status) ? (
          <Button size='sm' variant='primary' onClick={() => setBatchLine(line)}>
            Record delivery
          </Button>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
  ]

  return (
    <>
      <Modal black width='800px'>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center>
            <TitleText weight='bold'>
              Delivery request {request ? `#${request.id}` : ''}
            </TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>

          {!request ? (
            <OtherText grey>Loading…</OtherText>
          ) : (
            <>
              <Flex gap='1rem' wrap>
                <StatusPill status={statusTone(request.status)}>
                  {fuelDeliveryRequestStatusLabel(request.status)}
                </StatusPill>
                <OtherText grey>
                  Created {formatDateForDisplay(request.createdAt)}
                  {request.createdBy
                    ? ` by ${request.createdBy.firstName} ${request.createdBy.lastName}`
                    : ''}
                </OtherText>
              </Flex>
              <Flex gap='2rem' wrap>
                <Flex column gap='0.25rem'>
                  <OtherText grey>Department</OtherText>
                  <BodyText>{request.department?.name || '—'}</BodyText>
                </Flex>
                <Flex column gap='0.25rem'>
                  <OtherText grey>Supplier</OtherText>
                  <BodyText>{request.supplier?.name || '—'}</BodyText>
                </Flex>
                <Flex column gap='0.25rem'>
                  <OtherText grey>Notes</OtherText>
                  <BodyText>{request.notes || '—'}</BodyText>
                </Flex>
              </Flex>
              <HonestTable columns={lineColumns} data={request.lines || []} keyField='id' />
              {canEditOrCancel(request.status) && (
                <Flex gap='0.75rem' justend width='100%'>
                  <Button variant='secondary' onClick={handleCancel}>
                    Cancel request
                  </Button>
                  <Button variant='primary' onClick={() => setEditOpen(true)}>
                    Edit request
                  </Button>
                </Flex>
              )}
            </>
          )}
        </Flex>
      </Modal>
      {editOpen && request && (
        <FuelDeliveryRequestFormModal
          value={request}
          closeModal={() => {
            setEditOpen(false)
            refresh()
          }}
        />
      )}
      {batchLine && request && (
        <RecordFuelDeliveryBatchModal
          requestId={request.id}
          line={batchLine}
          closeModal={() => setBatchLine(null)}
          onSaved={refresh}
        />
      )}
    </>
  )
}
